import React from 'react';
import { Link } from 'react-router-dom';
import BACKEND_URL from 'constants/backendUrl';
import User from 'core/models/user';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSessionStore } from 'stores/session';
import { HttpError, POST } from 'utils/http';
import { toast } from 'react-toastify';
import { REGEXP } from 'constants/validationsForm';
import { TextInput, PasswordInput } from 'components/Forms/lib';
import useModal from 'hooks/modal';
import useQueryParams from 'utils/queryParams';
import BaseModal from 'components/Modals/BaseModal/BaseModal';
import Bloc from 'images/Bloc.svg?react';
import ResetPasswordForm from '../ResetPasswordForm';
import Button from 'components/Button';
import { SLinks } from './styles';

type Inputs = {
    email: string;
    password: string;
};

interface ResponseData {
    user: User;
}

/**
 * La page de connexion, fenêtre de réinitialisation du mot de passe ouverte : là où mène un lien
 * pour choisir son mot de passe qui ne fonctionne plus.
 */
export const RESET_PASSWORD_PATH = '/login?reset-password';

const loginFetcher = async (email: string, password: string) => {
    const response = (await POST(`${BACKEND_URL}/api/auth/login`, {
        email,
        password,
    })) as ResponseData;

    return response.user;
};

const LoginForm = () => {
    const { t } = useTranslation();
    const login = useSessionStore((s) => s.login);
    const queryParams = useQueryParams() as Record<string, unknown>;
    const { closeModal, openModal, isOpen } = useModal('reset-password' in queryParams);

    const methods = useForm<Inputs>({ mode: 'onSubmit' });
    const { register } = methods;

    const { handleSubmit } = methods;

    const onSubmit = handleSubmit(async (values: Inputs) => {
        const { email, password } = values;
        try {
            const user = await loginFetcher(email, password);
            login(user);
        } catch (error) {
            toast.error(
                t(`login-form.${(error as HttpError).status === 401 ? 'user-has-no-licence' : 'authentication-error'}`),
            );
        }
    });

    return (
        <>
            <FormProvider {...methods}>
                <form onSubmit={onSubmit}>
                    <TextInput
                        label={t('common.email.label')}
                        pattern={{ message: t(REGEXP.EMAIL.message) as string, value: REGEXP.EMAIL.value } as any}
                        placeholder={t('common.email.placeholder')}
                        type="email"
                        {...(register('email', { required: true }) as any)}
                        required
                    />
                    <PasswordInput
                        label={t('common.password.label')}
                        placeholder={t('common.password.placeholder')}
                        {...(register('password', { required: true }) as any)}
                        required
                    />
                    <Button onClick={openModal} variant="text" large>
                        {t('login-form.reset-password')}
                    </Button>
                    <Button large submit>
                        {t('login-form.submit')}
                    </Button>
                </form>
                <SLinks>
                    <Link rel="noopener noreferrer" target="_blank" title={t('legal-notice.label')} to="/legal-notice">
                        {t('legal-notice.label')}
                    </Link>
                    {'\u00a0-\u00a0'}
                    <Link rel="noopener noreferrer" target="_blank" title={t('cgu.label')} to="/cgu">
                        {t('cgu.acronym')}
                    </Link>
                    {'\u00a0-\u00a0'}
                    <Link rel="noopener noreferrer" target="_blank" title={t('cgv.label')} to="/cgv">
                        {t('cgv.acronym')}
                    </Link>
                </SLinks>
            </FormProvider>
            {isOpen && (
                <BaseModal
                    closeModal={() => closeModal()}
                    image={<Bloc />}
                    title={t('login-form.reset-password-modal-title')}
                >
                    <ResetPasswordForm closeModal={closeModal} />
                </BaseModal>
            )}
        </>
    );
};

export default LoginForm;
